/**
 * MTA cart enhancements (Dwell 4.2 / Horizon).
 *
 * 1. Cart drawer "These pair well with" recommendations, rendered through the
 *    Section Rendering API (sections/mta-cart-recs.liquid) and re-applied whenever
 *    Dwell morphs the drawer (cart line / discount / note updates, drawer open).
 * 2. Cart drawer "Estimate shipping" panel (GET /cart/shipping_rates.json, polls on 202).
 * 3. Cart page "Complete your set" add-to-cart forms (dispatch Dwell's CartLinesUpdateEvent
 *    so the drawer, cart page and header icon refresh natively).
 * 4. Cart page "Recently viewed" section (native localStorage.viewedProducts +
 *    /search?q=id:… section render, same trick as predictive search).
 *
 * Dwell events hooked: `shopify:cart:lines-update`, `shopify:cart:discount-update`,
 * `shopify:cart:note-update` (StandardEvents from @shopify/events) and `theme-drawer:open`.
 * A MutationObserver on #cart-drawer is the safety net for any morph we did not see coming.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const DRAWER_OPEN_EVENT = 'theme-drawer:open';
const RECS_LIMIT = 6;

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */

/**
 * @param {Function} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timer;
  return (/** @type {any[]} */ ...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  };
}

/**
 * Parses an HTML string and returns the first element matching `selector`.
 * @param {string} html
 * @param {string} selector
 * @returns {Element | null}
 */
function pick(html, selector) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector(selector);
}

/** @returns {string} */
function cartUrl() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
}

/** @returns {string} */
function cartAddUrl() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
}

/** @returns {string} */
function searchUrl() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search';
}

/* ------------------------------------------------------------------ */
/* 1. Drawer recommendations                                           */
/* ------------------------------------------------------------------ */

/** @type {Map<string, string | null>} html per product id (null = nothing to show) */
const recsCache = new Map();
/** @type {Map<string, Promise<string | null>>} */
const recsPending = new Map();

/**
 * Fetches the mta-cart-recs section for a product. Tries `complementary` first and
 * falls back to `related` when the complementary set is empty.
 * @param {HTMLElement} mount
 * @param {string} productId
 * @returns {Promise<string | null>}
 */
function fetchRecs(mount, productId) {
  if (recsCache.has(productId)) return Promise.resolve(recsCache.get(productId) ?? null);
  const pending = recsPending.get(productId);
  if (pending) return pending;

  const base = mount.dataset.recsUrl || '/recommendations/products';
  const sectionId = mount.dataset.sectionId || 'mta-cart-recs';

  /** @param {string} intent */
  const request = (intent) => {
    const url = new URL(base, location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(RECS_LIMIT));
    url.searchParams.set('intent', intent);
    return fetch(url.toString(), { headers: { Accept: 'text/html' } })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const el = pick(html, '[data-mta-recs]');
        return el ? el.outerHTML : null;
      })
      .catch(() => null);
  };

  const promise = request('complementary')
    .then((html) => html ?? request('related'))
    .then((html) => {
      recsCache.set(productId, html);
      recsPending.delete(productId);
      return html;
    });

  recsPending.set(productId, promise);
  return promise;
}

/**
 * Wires the horizontal track (prev/next buttons + "i/N" counter).
 * @param {HTMLElement} root
 */
function wireRecsTrack(root) {
  const track = /** @type {HTMLElement | null} */ (root.querySelector('[data-mta-recs-track]'));
  const counter = root.querySelector('[data-mta-recs-counter]');
  const prev = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-mta-recs-prev]'));
  const next = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-mta-recs-next]'));
  if (!track) return;

  const cards = () => /** @type {HTMLElement[]} */ (Array.from(track.querySelectorAll('[data-mta-recs-card]')));
  const total = cards().length;

  const step = () => {
    const first = cards()[0];
    if (!first) return track.clientWidth;
    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  };

  const index = () => Math.min(total - 1, Math.max(0, Math.round(track.scrollLeft / step())));

  const update = () => {
    if (!track.isConnected) return;
    const i = index();
    if (counter) counter.textContent = `${i + 1}/${total}`;
    const maxScroll = track.scrollWidth - track.clientWidth - 1;
    if (prev) prev.disabled = track.scrollLeft <= 0;
    if (next) next.disabled = track.scrollLeft >= maxScroll;
  };

  /** @param {number} dir */
  const go = (dir) => {
    track.scrollBy({ left: dir * step(), behavior: 'smooth' });
  };

  prev?.addEventListener('click', () => go(-1));
  next?.addEventListener('click', () => go(1));
  track.addEventListener('scroll', debounce(update, 40), { passive: true });
  window.addEventListener('resize', debounce(update, 120), { passive: true });
  // Keyboard: arrow keys while focus is inside the track.
  track.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') go(1);
    if (event.key === 'ArrowLeft') go(-1);
  });
  requestAnimationFrame(update);
}

/**
 * Idempotent: makes every recs mount reflect the current first cart line.
 */
function syncRecs() {
  const mounts = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('[data-mta-cart-recs]')));
  for (const mount of mounts) {
    const productId = mount.dataset.productId || '';
    const rendered = mount.querySelector('[data-mta-recs]');

    if (!productId) {
      mount.hidden = true;
      if (rendered) mount.replaceChildren();
      continue;
    }

    if (rendered && mount.dataset.renderedFor === productId) continue; // already up to date

    fetchRecs(mount, productId).then((html) => {
      // The cart may have changed while we were fetching.
      if (!mount.isConnected || mount.dataset.productId !== productId) return;
      if (!html) {
        mount.hidden = true;
        mount.replaceChildren();
        delete mount.dataset.renderedFor;
        return;
      }
      mount.innerHTML = html;
      mount.dataset.renderedFor = productId;
      mount.hidden = false;
      const root = /** @type {HTMLElement | null} */ (mount.querySelector('[data-mta-recs]'));
      if (root) wireRecsTrack(root);
    });
  }
}

/* ------------------------------------------------------------------ */
/* 2. Shipping estimator                                               */
/* ------------------------------------------------------------------ */

const shipState = {
  country: '',
  province: '',
  zip: '',
  /** @type {string} */
  resultsHtml: '',
  busy: false,
};

/**
 * @param {HTMLSelectElement} countrySelect
 * @param {HTMLSelectElement | null} provinceSelect
 * @param {HTMLElement | null} provinceWrap
 * @param {string} [selectedProvince]
 */
function populateProvinces(countrySelect, provinceSelect, provinceWrap, selectedProvince = '') {
  if (!provinceSelect) return;
  const option = countrySelect.selectedOptions[0];
  /** @type {Array<[string, string]>} */
  let provinces = [];
  try {
    provinces = JSON.parse(option?.getAttribute('data-provinces') || '[]');
  } catch (_) {
    provinces = [];
  }
  provinceSelect.replaceChildren();
  if (!provinces.length) {
    if (provinceWrap) provinceWrap.hidden = true;
    return;
  }
  for (const [value, label] of provinces) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    if (value === selectedProvince) opt.selected = true;
    provinceSelect.append(opt);
  }
  if (provinceWrap) provinceWrap.hidden = false;
}

/**
 * Re-applies remembered inputs and results after a drawer morph.
 */
function syncShipping() {
  const panels = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('[data-mta-ship]')));
  for (const panel of panels) {
    const country = /** @type {HTMLSelectElement | null} */ (panel.querySelector('[data-mta-ship-country]'));
    const province = /** @type {HTMLSelectElement | null} */ (panel.querySelector('[data-mta-ship-province]'));
    const provinceWrap = /** @type {HTMLElement | null} */ (panel.querySelector('[data-mta-ship-province-wrap]'));
    const zip = /** @type {HTMLInputElement | null} */ (panel.querySelector('[data-mta-ship-zip]'));
    const results = /** @type {HTMLElement | null} */ (panel.querySelector('[data-mta-ship-results]'));

    if (country) {
      if (!shipState.country) {
        const preferred = results?.dataset.defaultCountry || '';
        const match = Array.from(country.options).find(
          (o) => o.value === preferred || o.textContent?.trim() === preferred
        );
        shipState.country = match ? match.value : country.value;
      }
      if (country.value !== shipState.country) country.value = shipState.country;
      if (!country.dataset.mtaWired) {
        country.dataset.mtaWired = 'true';
        country.addEventListener('change', () => {
          shipState.country = country.value;
          shipState.province = '';
          populateProvinces(country, province, provinceWrap);
        });
      }
      populateProvinces(country, province, provinceWrap, shipState.province);
    }
    if (province && !province.dataset.mtaWired) {
      province.dataset.mtaWired = 'true';
      province.addEventListener('change', () => {
        shipState.province = province.value;
      });
    }
    if (zip) {
      if (zip.value !== shipState.zip) zip.value = shipState.zip;
      if (!zip.dataset.mtaWired) {
        zip.dataset.mtaWired = 'true';
        zip.addEventListener('input', () => {
          shipState.zip = zip.value;
        });
      }
    }
    if (results && results.innerHTML !== shipState.resultsHtml) {
      results.innerHTML = shipState.resultsHtml;
    }
    const submit = /** @type {HTMLButtonElement | null} */ (panel.querySelector('[data-mta-ship-submit]'));
    if (submit) submit.disabled = shipState.busy;
  }
}

/**
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Formats a shipping rate price. Uses Shopify's money format when available.
 * @param {{ price: string, currency?: string }} rate
 */
function formatRatePrice(rate) {
  const amount = Number(rate.price);
  if (!Number.isFinite(amount)) return escapeHtml(rate.price);
  if (amount === 0) return 'Free';
  const currency = rate.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch (_) {
    return `${amount.toFixed(2)} ${currency}`.trim();
  }
}

/**
 * @param {URL} url
 * @param {number} attempt
 * @returns {Promise<any>}
 */
async function fetchRates(url, attempt = 0) {
  const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
  if (response.status === 202) {
    if (attempt > 12) throw new Error('Shipping rates are taking too long. Please try again.');
    await new Promise((resolve) => setTimeout(resolve, 500 + attempt * 250));
    return fetchRates(url, attempt + 1);
  }
  let data = {};
  try {
    data = await response.json();
  } catch (_) {
    data = {};
  }
  if (!response.ok) {
    const messages = [];
    if (data && typeof data === 'object') {
      for (const value of Object.values(data)) {
        if (Array.isArray(value)) messages.push(...value.map(String));
        else if (typeof value === 'string') messages.push(value);
      }
    }
    throw new Error(messages.join(' ') || 'We could not estimate shipping for that address.');
  }
  return data;
}

/**
 * @param {SubmitEvent} event
 */
async function handleShippingSubmit(event) {
  const form = /** @type {HTMLFormElement} */ (event.target);
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-mta-ship-form')) return;
  event.preventDefault();

  const country = /** @type {HTMLSelectElement | null} */ (form.querySelector('[data-mta-ship-country]'));
  const province = /** @type {HTMLSelectElement | null} */ (form.querySelector('[data-mta-ship-province]'));
  const zip = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-mta-ship-zip]'));

  shipState.country = country?.value || '';
  shipState.province = province && !province.closest('[hidden]') ? province.value : '';
  shipState.zip = zip?.value.trim() || '';

  const url = new URL(`${cartUrl()}/shipping_rates.json`, location.origin);
  url.searchParams.set('shipping_address[country]', shipState.country);
  if (shipState.province) url.searchParams.set('shipping_address[province]', shipState.province);
  url.searchParams.set('shipping_address[zip]', shipState.zip);

  shipState.busy = true;
  shipState.resultsHtml = '<p class="mta-ship__note">Calculating…</p>';
  syncShipping();

  try {
    const data = await fetchRates(url);
    const rates = Array.isArray(data.shipping_rates) ? data.shipping_rates : [];
    if (!rates.length) {
      shipState.resultsHtml =
        '<p class="mta-ship__error">No shipping rates are available for that address. Try a different postcode.</p>';
    } else {
      const items = rates
        .map(
          (/** @type {any} */ rate) =>
            `<li class="mta-ship__rate"><span class="mta-ship__rate-name">${escapeHtml(rate.name || rate.presentment_name || '')}</span><span class="mta-ship__rate-price">${formatRatePrice(rate)}</span></li>`
        )
        .join('');
      shipState.resultsHtml = `<p class="mta-ship__note">Rates for ${escapeHtml(shipState.zip)}, ${escapeHtml(shipState.country)}:</p><ul class="mta-ship__rates list-unstyled">${items}</ul>`;
    }
  } catch (error) {
    shipState.resultsHtml = `<p class="mta-ship__error">${escapeHtml(error instanceof Error ? error.message : String(error))}</p>`;
  } finally {
    shipState.busy = false;
    syncShipping();
  }
}

/* ------------------------------------------------------------------ */
/* 3. Cart page add-ons ("Complete your set")                          */
/* ------------------------------------------------------------------ */

/**
 * @param {SubmitEvent} event
 */
async function handleAddonSubmit(event) {
  const form = /** @type {HTMLFormElement} */ (event.target);
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-mta-addon-form')) return;
  event.preventDefault();

  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
  const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
  const variantId = idInput?.value;
  if (!variantId) return;

  // Ask Shopify for every cart section on the page so Dwell can morph them from the response.
  const sectionIds = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
    if (id) sectionIds.add(id);
  }

  const formData = new FormData(form);
  formData.set('quantity', formData.get('quantity') || '1');
  if (sectionIds.size) {
    formData.set('sections', Array.from(sectionIds).join(','));
    formData.set('sections_url', window.location.pathname);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  if (button) {
    button.disabled = true;
    button.classList.add('mta-card__add--busy');
  }

  try {
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    const cart = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'mta-addons-row',
        sourceId: form.id || 'mta-addons',
        itemCount: 1,
        sections: data.sections,
        didError: false,
      },
    });
    if (button) {
      button.classList.add('mta-card__add--added');
      const label = button.querySelector('.mta-card__add-label');
      if (label) {
        const original = label.textContent;
        label.textContent = 'Added';
        setTimeout(() => {
          label.textContent = original;
          button.classList.remove('mta-card__add--added');
        }, 1800);
      }
    }
  } catch (error) {
    deferred.reject(error instanceof Error ? error : new Error(String(error)));
    const err = form.querySelector('[data-mta-addon-error]');
    if (err) {
      err.textContent = error instanceof Error ? error.message : String(error);
      err.removeAttribute('hidden');
      setTimeout(() => err.setAttribute('hidden', ''), 4000);
    }
  } finally {
    if (button) {
      button.disabled = false;
      button.classList.remove('mta-card__add--busy');
    }
  }
}

/* ------------------------------------------------------------------ */
/* 4. Recently viewed (cart page)                                      */
/* ------------------------------------------------------------------ */

class MtaRecentlyViewed extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    this.dataset.loaded = 'true';
    this.#load();
  }

  async #load() {
    const section = /** @type {HTMLElement | null} */ (this.closest('.shopify-section'));
    const ids = RecentlyViewed.getProducts().filter(Boolean);
    const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
    const wanted = ids.filter((id) => !exclude.includes(String(id))).slice(0, Number(this.dataset.max || 8));

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);

    if (!wanted.length) {
      // In the theme editor keep the server-rendered placeholders visible so the section can be styled.
      if (section && !designMode) section.hidden = true;
      return;
    }

    const url = new URL(searchUrl(), location.origin);
    url.searchParams.set('q', wanted.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', this.dataset.sectionId || 'mta-recently-viewed');

    try {
      const html = await fetch(url.toString(), { headers: { Accept: 'text/html' } }).then((r) => (r.ok ? r.text() : ''));
      const grid = pick(html, '[data-mta-rv-grid]');
      const mount = this.querySelector('[data-mta-rv-mount]');
      if (!grid || !mount || !grid.querySelector('[data-mta-rv-item]')) {
        if (section && !designMode) section.hidden = true;
        return;
      }
      mount.replaceChildren(grid);
      if (section) section.hidden = false;
      this.hidden = false;
    } catch (_) {
      if (section && !designMode) section.hidden = true;
    }
  }
}

if (!customElements.get('mta-recently-viewed')) {
  customElements.define('mta-recently-viewed', MtaRecentlyViewed);
}

/* ------------------------------------------------------------------ */
/* Wiring                                                              */
/* ------------------------------------------------------------------ */

const syncDrawer = debounce(() => {
  syncRecs();
  syncShipping();
}, 50);

/**
 * Dwell's cart events carry a `promise` that settles after the cart mutation and the
 * section morph. Re-sync after it settles (and again on the next frame for safety).
 * @param {Event & { promise?: Promise<unknown> }} event
 */
function onCartEvent(event) {
  const promise = event.promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(() => requestAnimationFrame(syncDrawer)).catch(() => syncDrawer());
  } else {
    syncDrawer();
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartEvent);
document.addEventListener(StandardEvents.cartDiscountUpdate, onCartEvent);
document.addEventListener(StandardEvents.cartNoteUpdate, onCartEvent);
document.addEventListener(DRAWER_OPEN_EVENT, () => syncDrawer());
document.addEventListener('submit', handleShippingSubmit);
document.addEventListener('submit', handleAddonSubmit);
window.addEventListener('pageshow', () => syncDrawer());

// Safety net: any morph of the drawer (including ones triggered by third-party apps)
// re-applies our client-rendered pieces. Our own writes are idempotent so this cannot loop.
const drawer = document.getElementById('cart-drawer');
if (drawer) {
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const target = /** @type {Element} */ (record.target instanceof Element ? record.target : record.target.parentElement);
      // Ignore our own rendered subtrees.
      if (target && target.closest('[data-mta-recs], [data-mta-ship-results], [data-mta-ship-province]')) continue;
      syncDrawer();
      return;
    }
  });
  observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-product-id', 'open'] });
  if (drawer.hasAttribute('open')) syncDrawer();
}
